import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ServiceDeliveryLocationsResponse,
  ServiceDeliveryLocationsService,
  ServiceDeliveryLocationDetails,
  PopSummary
} from './service-delivery-locations.service';
import { CompanySelectionService } from './company-selection.service';
import { CompanySummary } from '../models/company-summary';

@Injectable({
  providedIn: 'root'
})
export class ServiceDeliveryLocationsFacade {
  private readonly companiesSubject = new BehaviorSubject<CompanySummary[]>([]);
  private readonly companiesLoadingSubject = new BehaviorSubject<boolean>(false);
  private readonly companiesErrorSubject = new BehaviorSubject<string | null>(null);

  private readonly selectedCompanySubject = new BehaviorSubject<CompanySummary | null>(null);

  private readonly popsSubject = new BehaviorSubject<PopSummary[]>([]);
  private readonly popsLoadingSubject = new BehaviorSubject<boolean>(false);
  private readonly popsErrorSubject = new BehaviorSubject<string | null>(null);

  private readonly selectedPopSubject = new BehaviorSubject<PopSummary | null>(null);

  private readonly popDetailsSubject = new BehaviorSubject<ServiceDeliveryLocationDetails | null>(null);
  private readonly popDetailsLoadingSubject = new BehaviorSubject<boolean>(false);
  private readonly popDetailsErrorSubject = new BehaviorSubject<string | null>(null);

  private readonly errorMessageSubject = new BehaviorSubject<string | null>(null);

  readonly companies$ = this.companiesSubject.asObservable();
  readonly companiesLoading$ = this.companiesLoadingSubject.asObservable();
  readonly companiesError$ = this.companiesErrorSubject.asObservable();

  readonly selectedCompany$ = this.selectedCompanySubject.asObservable();

  readonly companyPops$ = this.popsSubject.asObservable();
  readonly companyPopsLoading$ = this.popsLoadingSubject.asObservable();
  readonly companyPopsError$ = this.popsErrorSubject.asObservable();

  readonly mappablePops$: Observable<PopSummary[]> = this.companyPops$.pipe(
    map(pops => pops.filter(pop => typeof pop.latitude === 'number' && typeof pop.longitude === 'number'))
  );

  readonly selectedPop$ = this.selectedPopSubject.asObservable();

  readonly popDetails$ = this.popDetailsSubject.asObservable();
  readonly popDetailsLoading$ = this.popDetailsLoadingSubject.asObservable();
  readonly popDetailsError$ = this.popDetailsErrorSubject.asObservable();

  readonly errorMessage$ = this.errorMessageSubject.asObservable();

  constructor(
    private readonly service: ServiceDeliveryLocationsService,
    private readonly companySelectionService: CompanySelectionService
  ) {}

  resetState(): void {
    this.companiesSubject.next([]);
    this.companiesLoadingSubject.next(false);
    this.companiesErrorSubject.next(null);
    this.selectedCompanySubject.next(null);
    this.resetLocationData();
    this.errorMessageSubject.next(null);
  }

  loadCompanies(): void {
    this.companiesLoadingSubject.next(true);
    this.companiesErrorSubject.next(null);
    this.updateGlobalError();

    this.companySelectionService.loadCompanies().subscribe({
      next: (companies) => {
        this.companiesSubject.next(companies);
        this.companiesLoadingSubject.next(false);
      },
      error: () => {
        this.companiesErrorSubject.next('Unable to load companies.');
        this.companiesLoadingSubject.next(false);
        this.updateGlobalError();
      }
    });
  }

  selectCompany(company: CompanySummary): void {
    const current = this.selectedCompanySubject.value;
    if (current?.id === company.id) {
      return;
    }

    this.selectedCompanySubject.next(company);
    this.resetLocationData();
    this.updateGlobalError();
    this.loadCompanyLocations(company.id);
  }

  clearSelection(): void {
    this.selectedCompanySubject.next(null);
    this.resetLocationData();
    this.updateGlobalError();
  }

  selectPop(pop: PopSummary): void {
    const current = this.selectedPopSubject.value;
    if (current?.id === pop.id) {
      return;
    }

    this.selectedPopSubject.next(pop);
    const company = this.selectedCompanySubject.value;
    if (!company) {
      return;
    }

    this.loadLocationDetails(company.id, pop.id);
  }

  loadCompanyLocations(companyId: number): void {
    this.popsLoadingSubject.next(true);
    this.popsErrorSubject.next(null);
    this.updateGlobalError();

    this.service.getCompanyLocations(companyId).subscribe({
      next: (response: ServiceDeliveryLocationsResponse) => {
        const locations = response.locations ?? response.pops ?? [];
        this.popsSubject.next(locations);
        this.popsLoadingSubject.next(false);
      },
      error: () => {
        this.popsErrorSubject.next('Unable to retrieve service delivery locations for this company.');
        this.popsLoadingSubject.next(false);
        this.updateGlobalError();
      }
    });
  }

  loadLocationDetails(companyId: number, popId: number): void {
    this.popDetailsLoadingSubject.next(true);
    this.popDetailsErrorSubject.next(null);
    this.updateGlobalError();

    this.service.getLocationDetails(companyId, popId).subscribe({
      next: (details: ServiceDeliveryLocationDetails) => {
        this.popDetailsSubject.next(details);
        this.popDetailsLoadingSubject.next(false);
      },
      error: () => {
        this.popDetailsErrorSubject.next('Unable to load service delivery location details.');
        this.popDetailsLoadingSubject.next(false);
        this.updateGlobalError();
      }
    });
  }

  private resetLocationData(): void {
    this.popsSubject.next([]);
    this.popsLoadingSubject.next(false);
    this.popsErrorSubject.next(null);
    this.selectedPopSubject.next(null);
    this.popDetailsSubject.next(null);
    this.popDetailsLoadingSubject.next(false);
    this.popDetailsErrorSubject.next(null);
  }

  private updateGlobalError(): void {
    const message = this.companiesErrorSubject.value
      ?? this.popsErrorSubject.value
      ?? this.popDetailsErrorSubject.value;
    this.errorMessageSubject.next(message);
  }
}
